import { Suspense, useEffect, useState, FC, useRef, useCallback, useId } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import debugModule from 'debug'

// evertel
import { MessageList } from './MessageList'
import {
    CreateMessage,
    ForwardMessageModal,
    MessageListHeader,
    ReactedByModal,
    ReadByModal
} from './elements'
import { useService } from '@evertel/di'
import { MessageWallController, MessageWallContext, MessageWallContextProps } from '@evertel/message'
import { Col, Spinner, InfoBox, Button, Icon } from '@evertel/web/ui'
import { EmojiPickerModal } from '@evertel/web/emojis'
import { CurrentUserEmojisController } from '@evertel/emojis'
import { APIDataRoom, APIDataThread } from '@evertel/types'
import { debounce } from 'lodash'
import { DisplayRoomMessage } from '@evertel/stores'
import { FetchUserService } from '@evertel/blue-user'
import { RoomConnectorsBanner } from '@evertel/web/room'

const debug = debugModule('app:MessageWall:web')
debug.enabled = false


interface MessageWallProps {
    modelId: number
    modelType: 'thread' | 'room'
    modelData: APIDataRoom | APIDataThread
    allowedToPost: boolean
    repliesToMessage?: DisplayRoomMessage
}

const MessageWall: FC<MessageWallProps> = observer(({
    modelId,
    modelType,
    modelData,
    allowedToPost,
    repliesToMessage
}) => {

    const { repliesToId: repliesToIdString } = useParams<{repliesToId?: string}>()
    const repliesToId = repliesToIdString ? parseInt(repliesToIdString, 10) : null

    const [searchParams, setSearchParams] = useSearchParams()
    const targetMsgId = searchParams.get('msgId') ? parseInt(searchParams.get('msgId'), 10) : null
    const hasJumpParam = searchParams.has('jump')

    const messageWallController = useService(MessageWallController, [modelType, modelId])
    const fetchUserService = useService(FetchUserService, [])
    const currentUserEmojisController = useService(CurrentUserEmojisController, [])

    const msgWallId = useId()
    const [isLoading, setIsLoading] = useState(false)
    const [selectedMessageId, setSelectedMessageId] = useState(0)

    // modals
    const [emojiPickerState, setEmojiPickerState] = useState({ visible: false, params: {} })
    const [isReactedByModalOpen, setIsReactedByModalOpen] = useState(false)
    const [isReadByModalOpen, setIsReadByModalOpen] = useState(false)
    const [isForwardMessageModalOpen, setIsForwardMessageModalOpen] = useState(false)
    const [focusedMessageId, setFocusedMessageId] = useState<number | null>(null)
    const [jumpToMessageId, setJumpToMessageId] = useState<number | null>(null)
    const [isAtBottom, setIsAtBottom] = useState(true)

    const createMessageRef = useRef<{ focus: () => void }>(null)
    const messageListRef = useRef<HTMLDivElement>(null)
    const jumpAttemptedRef = useRef(false)

    const isRoom = modelType === 'room'

    const contextValues: MessageWallContextProps = {
        modelId,
        modelType,
        modelData,
        repliesToId,
        allowedToPost,
        messageWallController,
        currentUserEmojisController,
        setSelectedMessageId,
        setIsReactedByModalOpen,
        setEmojiPickerState,
        setIsReadByModalOpen,
        setIsForwardMessageModalOpen,
        emojiPickerState,
        selectedMessageId,
        focusedMessageId,
        setFocusedMessageId,
        jumpToMessageId,
        setJumpToMessageId
    }

    useEffect(() => {
        if (!modelId) return undefined

        setIsLoading(true)
        debug(`🔵 init useEffect: modelType=${modelType}, modelId=${modelId}, repliesToId=${repliesToId}, targetMsgId=${targetMsgId}`)

        const getMessages = debounce(async () => {
            debug(`🔵 getMessages (debounced): starting fetchMessages('fresh'), targetMsgId=${targetMsgId}`)
            try {
                await messageWallController.fetchMessages('fresh')
                debug(`🔵 getMessages: fetchMessages('fresh') complete, messages in store: ${messageWallController.messagesSorted?.length}, activeView=${messageWallController.activeView}`)
            } finally {
                debug(`🔵 getMessages: setting isLoading=false`)
                setIsLoading(false)
            }
        }, 100)

        messageWallController.init(modelId, modelType, repliesToId, msgWallId)
        debug(`🔵 init complete, starting debounced getMessages`)
        getMessages()

        return () => {
            debug(`🔵 cleanup: cancelling getMessages, destroying controller`)
            getMessages.cancel()
            messageWallController.destroy()
        }
    }, [modelId, modelType, repliesToId])

    // Deep link / search jump: navigate to a specific message via ?msgId= query param
    // - `jumpAttemptedRef` prevents re-triggering on isLoading toggles after a jump completes
    // - `&jump` param is a transient signal (from search modal links) that resets the ref,
    //   enabling re-jump to the same message. It's stripped from the URL after execution.
    // - Fresh page load: ref starts false, no &jump needed → jumps on first isLoading=false
    // - Search click: <Link> includes &jump → resets ref → jumps → strips &jump from URL
    // - Re-click same search result: &jump re-added to URL → ref reset → jumps again
    useEffect(() => {
        // Reset the ref when &jump param is present (search modal navigation)
        if (hasJumpParam && jumpAttemptedRef.current) {
            jumpAttemptedRef.current = false
        }

        debug(`🔵 jumpToMessage useEffect: targetMsgId=${targetMsgId}, isLoading=${isLoading}, jumpAttempted=${jumpAttemptedRef.current}, hasJumpParam=${hasJumpParam}`)
        if (!targetMsgId || isLoading || jumpAttemptedRef.current) {
            debug(`🔵 jumpToMessage useEffect: skipping (targetMsgId=${targetMsgId}, isLoading=${isLoading}, jumpAttempted=${jumpAttemptedRef.current})`)
            return
        }

        jumpAttemptedRef.current = true

        debug(`🔵 jumpToMessage useEffect: calling jumpToMessage(${targetMsgId}), activeView=${messageWallController.activeView}, primary msgs=${messageWallController.getMessagesForContext('primary')?.length}, anchor msgs=${messageWallController.getMessagesForContext('anchor')?.length}`)
        messageWallController.jumpToMessage(targetMsgId).then((msgId) => {
            debug(`🔵 jumpToMessage resolved: msgId=${msgId}, activeView=${messageWallController.activeView}`)
            if (msgId) {
                setJumpToMessageId(msgId)
            }
            // Strip the transient &jump param, keep &msgId for bookmarkability
            if (hasJumpParam) {
                setSearchParams((prev) => {
                    prev.delete('jump')
                    return prev
                }, { replace: true })
            }
        })
    }, [targetMsgId, isLoading, hasJumpParam])

    useEffect(() => {
        // retrieve the user and department of the replies to message, and forwardedOwner if there is one
        if (repliesToMessage?.ownerId)  {
            const forwardedOwnerId = messageWallController.processForwardedMessage([repliesToMessage])
            fetchUserService.fetchSpecificUsersFromRoom(messageWallController.modelId, [repliesToMessage?.ownerId, ...forwardedOwnerId])
        }
    }, [repliesToMessage])

    useEffect(() => {
        if (allowedToPost && createMessageRef.current) {
            createMessageRef.current.focus()
        } else if (!allowedToPost && messageListRef.current) {
            messageListRef.current.focus()
        }
    }, [modelId, allowedToPost])

    const handleStartReached = useCallback(async () => {
        if (isLoading) return
        debug(`🔵 handleStartReached: activeView=${messageWallController.activeView}, primary msgs=${messageWallController.getMessagesForContext('primary')?.length}, anchor msgs=${messageWallController.getMessagesForContext('anchor')?.length}`)
        setIsLoading(true)
        try {
            await messageWallController.fetchMessages('older')
        } finally {
            setIsLoading(false)
        }
    }, [messageWallController, isLoading])

    const handleEndReached = useCallback(async () => {
        if (isLoading) return
        debug(`🔵 handleEndReached: activeView=${messageWallController.activeView}, primary msgs=${messageWallController.getMessagesForContext('primary')?.length}, anchor msgs=${messageWallController.getMessagesForContext('anchor')?.length}`)
        // Only fetch newer messages when viewing the anchor window (filling gap toward primary)
        if (messageWallController.activeView !== 'anchor') return

        debug(`🔵 handleEndReached: proceeding with fetchMessages('newer')`)
        setIsLoading(true)
        try {
            await messageWallController.fetchMessages('newer')
        } finally {
            setIsLoading(false)
        }
    }, [messageWallController, isLoading])

    const handleJumpToPresent = useCallback(() => {
        if (messageWallController.activeView === 'anchor') {
            messageWallController.jumpToPresent()
        }
        // Scroll to the newest message in the (now active) primary window
        const primaryMessages = messageWallController.messagesVisible
        if (primaryMessages?.length) {
            setJumpToMessageId(primaryMessages[primaryMessages.length - 1].id)
        }
    }, [messageWallController])

    const listHeaderComponentMemo = useCallback(() => (
        // <div style={{ height: 200 }}><h1>I AM THE HEADER</h1></div>
        <>
            {/* {`sorted:${messageWallController.messagesSorted?.length}  ---  Count ${messageWallController.messagesCount}`} */}
            <MessageListHeader
                isLoading={isLoading}
                message={repliesToMessage}
            />
        </>
    ), [isLoading, messageWallController.messagesSorted, repliesToMessage])


    if (messageWallController.errorFetching) {
        return (
            <Col className="no-messages pe-3">
                <InfoBox
                    color="warning"
                    className="m-3"
                >
                    Error retrieving messages for this {isRoom ? 'Room' : 'DM'}.
                </InfoBox>
            </Col>
        )
    }

    return (
        <MessageWallContext.Provider value={contextValues}>
            <Col
                ref={messageListRef}
                className={classNames('message-list', { 'opacity-75': (modelData as APIDataRoom)?.isArchived })}
                tabIndex={-1}
                style={{ position: 'relative' }}
            >
                <MessageList
                    // key={`${modelType} - ${modelId} - ${repliesToId}`}
                    modelId={modelId}
                    modelType={modelType}
                    repliesToId={repliesToId}
                    messages={messageWallController.messagesWithDates}
                    onStartReached={handleStartReached}
                    onEndReached={handleEndReached}
                    onAtBottomStateChange={setIsAtBottom}
                    listHeaderComponent={listHeaderComponentMemo}
                />

                {/* Jump to Present / scroll to bottom button */}
                {(messageWallController.activeView === 'anchor' || !isAtBottom) && (
                    <div className="jump-to-present-anchor">
                        <Button
                            color="primary"
                            pill
                            onClick={handleJumpToPresent}
                            aria-label={messageWallController.activeView === 'anchor' ? 'Jump to present' : 'Scroll to bottom'}
                            className="jump-to-present-btn"
                        >
                            <Icon name="arrow-down" size={16} aria-hidden />
                            {' Jump to Present'}
                        </Button>
                    </div>
                )}

                {isRoom && <RoomConnectorsBanner />}
                <CreateMessage ref={createMessageRef} />

                <ReactedByModal
                    visible={isReactedByModalOpen}
                    modelType={modelType}
                    onClose={() => setIsReactedByModalOpen(false)}
                    onCloseDone={() => setSelectedMessageId(0)}
                    messageId={selectedMessageId}
                />
                <ReadByModal
                    visible={isReadByModalOpen}
                    modelType={modelType}
                    onClose={() => setIsReadByModalOpen(false)}
                    onCloseDone={() => setSelectedMessageId(0)}
                    messageId={selectedMessageId}
                />
                <ForwardMessageModal
                    visible={isForwardMessageModalOpen}
                    onClose={() => setIsForwardMessageModalOpen(false)}
                    messageId={selectedMessageId}
                />
                <EmojiPickerModal
                    visible={emojiPickerState.visible}
                    onSelectEmoji={(emojiPickerState.params as any)?.onSelectEmoji}
                    onClose={() => setEmojiPickerState({ visible: false, params: {} })}
                />
            </Col>
        </MessageWallContext.Provider>
    )
})

MessageWall.displayName = 'MessageWall'

export { MessageWall }
